import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, X, Minimize2 } from 'lucide-react';
import * as Icons from 'lucide-react';
import { useCategories } from '../hooks/useCategories';
import { useHabitSessionStore } from '../stores/habitSessionStore';
import { formatPoints } from '../utils/levelCalculation';
import type { Habit } from '../types';

interface HabitStopwatchSessionProps {
  habit: Habit;
  onComplete: (durationSeconds: number, xpEarned: number) => void;
  onMinimize: () => void;
  onCancel: () => void;
}

export function HabitStopwatchSession({ habit, onComplete, onMinimize, onCancel }: HabitStopwatchSessionProps) {
  const [isPaused, setIsPaused] = useState(false);
  const [showConfirmExit, setShowConfirmExit] = useState(false);
  const [laps, setLaps] = useState<number[]>([]);

  const { activeSession, calculateSessionXP, updateElapsedTime } = useHabitSessionStore();
  const { getCategoryColor, getCategoryName } = useCategories();
  const timerRef = useRef<number>();

  const categoryColor = getCategoryColor(habit.category);
  const HabitIcon = habit.icon && (Icons[habit.icon as keyof typeof Icons] as any)
    ? (Icons[habit.icon as keyof typeof Icons] as any)
    : Icons.Star;

  const elapsedSeconds = activeSession?.elapsedSeconds || 0;
  const currentXP = calculateSessionXP(elapsedSeconds);

  useEffect(() => {
    if (isPaused) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      return;
    }

    timerRef.current = window.setInterval(() => {
      updateElapsedTime();
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isPaused]);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleComplete = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    const finalXP = calculateSessionXP(elapsedSeconds);
    onComplete(elapsedSeconds, finalXP);
  };

  const handleStop = () => {
    setShowConfirmExit(true);
  };

  const confirmStop = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    const finalXP = calculateSessionXP(elapsedSeconds);
    onComplete(elapsedSeconds, finalXP);
  };

  const handleLap = () => {
    setLaps([...laps, elapsedSeconds]);
  };

  return (
    <div className="fixed inset-0 bg-background z-50 flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div
            className="bg-card rounded-lg border border-border p-8"
            style={{ backgroundColor: `${categoryColor}05` }}
          >
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <HabitIcon className="w-8 h-8" style={{ color: categoryColor }} />
                <div>
                  <h2 className="text-2xl font-bold text-foreground">
                    {habit.name}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {getCategoryName(habit.category)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={onMinimize}
                  className="p-2 hover:bg-muted rounded-md transition-colors"
                  title="Minimize"
                >
                  <Minimize2 className="w-5 h-5 text-muted-foreground" />
                </button>
                <button
                  onClick={handleStop}
                  className="p-2 hover:bg-muted rounded-md transition-colors"
                  title="Stop Session"
                >
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>
            </div>

            <div className="text-center mb-8">
              <div className="text-7xl font-bold mb-4" style={{ color: categoryColor }}>
                {formatTime(elapsedSeconds)}
              </div>
              <p className="text-sm text-muted-foreground">
                Time Elapsed
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="p-4 bg-background rounded-lg border border-border">
                <p className="text-sm text-muted-foreground mb-1">Current XP</p>
                <p
                  className="text-2xl font-semibold"
                  style={{ color: categoryColor }}
                >
                  {formatPoints(currentXP)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {elapsedSeconds < 60 ? 'Base XP only' : `+5 XP/min`}
                </p>
              </div>
              <div className="p-4 bg-background rounded-lg border border-border">
                <p className="text-sm text-muted-foreground mb-1">Laps</p>
                <p className="text-2xl font-semibold text-foreground">
                  {laps.length}
                </p>
              </div>
            </div>

            {laps.length > 0 && (
              <div className="mb-6 max-h-32 overflow-y-auto bg-background rounded-lg border border-border p-3">
                <div className="space-y-1">
                  {laps.map((lapTime, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Lap {index + 1}</span>
                      <span className="font-mono text-foreground">
                        {formatTime(lapTime)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => setIsPaused(!isPaused)}
                className="p-6 rounded-full hover:bg-muted transition-colors"
                style={{ color: categoryColor }}
              >
                {isPaused ? (
                  <Play className="w-10 h-10" fill={categoryColor} />
                ) : (
                  <Pause className="w-10 h-10" />
                )}
              </button>
              <button
                onClick={handleLap}
                disabled={isPaused}
                className="px-4 py-3 rounded-lg font-semibold border-2 transition-colors disabled:opacity-50"
                style={{
                  borderColor: categoryColor,
                  color: categoryColor
                }}
              >
                Lap
              </button>
              <button
                onClick={handleComplete}
                className="px-6 py-3 rounded-full font-semibold text-white transition-colors"
                style={{ backgroundColor: categoryColor }}
              >
                Complete Session
              </button>
            </div>

            {elapsedSeconds >= 300 && elapsedSeconds % 300 === 0 && (
              <div className="mt-6 p-4 bg-background rounded-lg border border-border text-center">
                <p className="text-sm font-medium" style={{ color: categoryColor }}>
                  {Math.floor(elapsedSeconds / 60)} minutes completed! Keep going!
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showConfirmExit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg border border-border p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Stop Session?
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
              Your progress will be saved and you'll earn XP based on the time completed.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmExit(false)}
                className="flex-1 px-4 py-2 border border-input rounded-md hover:bg-muted transition-colors"
              >
                Continue
              </button>
              <button
                onClick={confirmStop}
                className="flex-1 px-4 py-2 rounded-md text-white transition-colors"
                style={{ backgroundColor: categoryColor }}
              >
                Stop & Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
